import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import { ArrowLeft, ArrowUpRight, Plus, Minus, MapPin, Check, GraduationCap, Sparkles, Crown, CreditCard, Calendar, RefreshCw, Info } from "lucide-react";
import FloatingBookButton from "../components/FloatingBookButton";

// ── CONFIGURABLE ENROLLMENT FORM URL — replace this single value to update all CTAs ──
const JOTFORM_URL = "https://form.jotform.com/262321485292053";

const BOOK_LINK = "https://www.fresha.com/p/mwezi-mjoli-4489930?share=true&pId=1469337";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/fdlrkebv_WhatsApp%20Image%202025-11-21%20at%2012.44.06_ff66975e.jpg";

const IMG = {
  hero: "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/wwyse4y2_Screenshot_20260318_170752_Instagram.jpg",
  intro: "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/hlu2seob_Screenshot_20260318_170649_Instagram.jpg",
  eligibility: "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/pigj0doi_Screenshot_20260318_164623_Instagram.jpg",
};

// Masked line reveal
const MaskLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, y = 40, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

// Pricing Card
const PlanCard = ({ plan, index }) => {
  const featured = plan.featured;
  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className={`relative rounded-2xl border p-8 md:p-10 flex flex-col transition-all duration-500
        ${featured
          ? 'border-[#D4AF37] bg-gradient-to-b from-[#171310] to-[#0a0a0a] md:-mt-6 md:mb-6 shadow-[0_30px_70px_-25px_rgba(212,175,55,0.3)]'
          : 'border-white/10 bg-[#0d0d0d]'}
        hover:border-[#D4AF37]/60 hover:-translate-y-2`}
    >
      {featured && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#050505] text-[10px] font-bold uppercase tracking-[0.25em] px-4 py-1.5 rounded-full">
          Most Popular
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <plan.icon className="w-5 h-5 text-[#D4AF37]" />
        <h3 className="font-serif text-xl text-white">{plan.name}</h3>
      </div>

      <div className="flex items-baseline gap-1 mb-4">
        <span className="font-serif text-5xl text-white tracking-tight">{plan.price}</span>
        <span className="text-white/40 text-sm">/ month</span>
      </div>

      <p className="text-white/50 text-sm font-light mb-8 leading-relaxed">{plan.description}</p>

      <div className="space-y-3 mb-8 flex-grow">
        {plan.sessionNote && (
          <p className="text-[#D4AF37] text-xs uppercase tracking-widest mb-3">{plan.sessionNote}</p>
        )}
        {plan.included.map((item, i) => (
          <div key={i} className="flex items-start gap-3 text-white/70 text-sm">
            <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <span>{item}</span>
          </div>
        ))}
      </div>

      <a
        href={JOTFORM_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`plan-btn-${plan.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
        className={`group flex items-center justify-between w-full px-6 py-4 rounded-full text-xs font-semibold uppercase tracking-widest transition-all duration-300
          ${featured ? 'bg-[#D4AF37] text-[#050505] hover:bg-[#e8c65a]' : 'border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505]'}`}
      >
        {plan.button}
        <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </a>
    </motion.div>
  );
};

const FaqItem = ({ q, a, isOpen, onClick, index }) => (
  <div className="border-b border-white/10">
    <button
      onClick={onClick}
      data-testid={`faq-item-${index}`}
      className="w-full flex items-center justify-between py-5 text-left group gap-6"
    >
      <span className={`font-serif text-base md:text-lg transition-colors duration-300 ${isOpen ? 'text-[#D4AF37]' : 'text-white group-hover:text-[#D4AF37]'}`}>
        {q}
      </span>
      <span className="flex-shrink-0 text-[#D4AF37]">
        {isOpen ? <Minus className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
      </span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <p className="text-white/50 font-light leading-relaxed pb-6 pr-10 max-w-2xl">{a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const CTA = ({ children, primary, testid, href = JOTFORM_URL }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-xs font-semibold uppercase tracking-widest transition-all duration-300 hover:-translate-y-1
      ${primary ? 'bg-[#D4AF37] text-[#050505] hover:bg-[#e8c65a]' : 'border border-white/20 text-white hover:border-[#D4AF37] hover:text-[#D4AF37]'}`}
  >
    {children}
    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
  </a>
);

const StudentMembershipPage = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const heroRef = useRef(null);

  useEffect(() => {
    document.title = "Carlocs Student Membership | Affordable Loc Care in Johannesburg";
    const lenis = new Lenis({ duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    const id = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(id); lenis.destroy(); document.title = "Loctician Carlocs | Luxury Loc Care in South Africa"; };
  }, []);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const plans = [
    {
      name: "Student Essential",
      icon: GraduationCap,
      price: "R349",
      description: "For students who want simple, consistent loc maintenance.",
      included: [
        "1 Retwist & Style per billing period",
        "All loc lengths",
        "Johannesburg CBD",
        "Braamfontein",
      ],
      button: "Choose Essential",
    },
    {
      name: "Student Care",
      icon: Sparkles,
      price: "R449",
      featured: true,
      description: "Our balanced plan for students who want more flexibility.",
      sessionNote: "1 eligible session / billing period",
      included: [
        "Choose: Retwist & Style, Crochet Maintenance or Loc Treatment",
        "10% off styles with extensions",
        "5% off ACV services",
        "5% off hair colouring",
        "All loc lengths included",
        "Johannesburg CBD & Braamfontein",
      ],
      button: "Choose Care",
    },
    {
      name: "Student Premium",
      icon: Crown,
      price: "R549",
      description: "For students who want the most value and flexibility.",
      sessionNote: "1 eligible session / billing period",
      included: [
        "Choose: Retwist & Style, Crochet Maintenance or Loc Treatment",
        "15% off styles with extensions",
        "10% off ACV services",
        "10% off hair colouring",
        "5% off Carlocs Culture products",
        "All loc lengths · Both locations",
      ],
      button: "Choose Premium",
    },
  ];

  const steps = [
    { num: "01", title: "Choose Your Plan", desc: "Select Essential, Care or Premium." },
    { num: "02", title: "Set Up Your Payment", desc: "Your selected membership fee is charged monthly through PayFast recurring billing." },
    { num: "03", title: "Verify", desc: "Carlocs verifies your student status and first successful payment." },
    { num: "04", title: "Activate", desc: "Your corresponding monthly Fresha package is manually activated." },
    { num: "05", title: "Book", desc: "Book your appointment through Fresha." },
    { num: "06", title: "Enjoy Your Loc Care", desc: "Use your monthly eligible benefit and enjoy your member discounts." },
  ];

  const membBenefits = [
    { title: "Monthly Loc Care", desc: "Stay consistent with your loc maintenance.", icon: RefreshCw },
    { title: "Flexible Service Options", desc: "Care and Premium members can choose their eligible monthly service.", icon: Sparkles },
    { title: "Member-Only Savings", desc: "Enjoy discounts on selected styling, ACV, colouring and Carlocs Culture products.", icon: Crown },
    { title: "Two Locations", desc: "Use your membership at Johannesburg CBD or Braamfontein.", icon: MapPin },
  ];

  const faqs = [
    { q: "Is the membership available for all loc lengths?", a: "Yes. All loc lengths are included. Membership pricing does not change based on whether your locs are short, medium or long." },
    { q: "How does the monthly payment work?", a: "Membership fees are recurring monthly payments processed through PayFast. You authorize the recurring payment when joining your selected plan." },
    { q: "When does my membership become active?", a: "Your membership becomes active after your student status and first successful PayFast payment have been verified. Your corresponding Fresha package is then manually activated." },
    { q: "Can I use my membership at both locations?", a: "Yes. Your membership can be used at both Johannesburg CBD and Braamfontein." },
    { q: "Can I use more than one included service in a month?", a: "No. Your membership provides one eligible loc-care session per billing period. Care and Premium members choose one eligible service for that month's included session." },
    { q: "Do unused sessions roll over?", a: "No. Unused monthly sessions do not automatically roll over to the next billing period." },
    { q: "Are styles with extensions included?", a: "No. Styles with extensions are not included as the monthly maintenance benefit. Eligible members receive a discount on qualifying extension styles according to their membership tier. Hair, extensions and additional materials remain separately chargeable." },
    { q: "Are ACV services and hair colouring included?", a: "ACV services and hair colouring are not automatically included as the monthly maintenance session. Care and Premium members receive the applicable discount when booking eligible services." },
    { q: "Do I need to prove that I am a student?", a: "Yes. Current student verification is required to qualify for Carlocs Student Membership." },
    { q: "Can I cancel my membership?", a: "Yes. Members can request cancellation before their next scheduled recurring payment, subject to the membership terms and PayFast recurring payment process." },
  ];

  return (
    <div className="bg-[#050505] text-white overflow-x-hidden">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 bg-black/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Loctician Carlocs - Johannesburg loctician logo" className="h-11 w-auto" />
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="hidden sm:flex items-center gap-2 text-white/60 hover:text-[#D4AF37] transition-colors text-xs uppercase tracking-widest">
              <ArrowLeft className="w-4 h-4" /> Home
            </Link>
            <a href={JOTFORM_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-join-btn"
              className="bg-[#D4AF37] text-[#050505] px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-widest hover:bg-[#e8c65a] transition-colors">
              Join
            </a>
          </div>
        </div>
      </nav>

      {/* SECTION 1 — HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden">
        <motion.div style={{ y: heroImgY }} className="absolute inset-0 -z-10">
          <img src={IMG.hero} alt="Johannesburg locs care" className="w-full h-[120%] object-cover object-center opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/75 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />
        </motion.div>

        <motion.div style={{ opacity: heroOpacity }} className="max-w-7xl mx-auto px-6 pt-32 pb-20 w-full">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
            className="text-[#D4AF37] text-xs uppercase tracking-[0.4em] mb-8">
            Carlocs Student Membership
          </motion.p>

          <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight mb-8">
            <MaskLine delay={0.3}>Premium Loc Care.</MaskLine>
            <MaskLine delay={0.45}><span className="italic text-[#D4AF37]">Student-Friendly Pricing.</span></MaskLine>
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
            className="text-white/60 text-base md:text-lg font-light max-w-xl mb-8 leading-relaxed">
            Consistent loc care made more affordable for students, with flexible monthly plans and member-only benefits.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
            className="flex flex-wrap gap-x-8 gap-y-3 mb-10 text-sm text-white/70">
            <span className="flex items-center gap-2"><GraduationCap className="w-4 h-4 text-[#D4AF37]" /> Available to current students</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#D4AF37]" /> Johannesburg CBD + Braamfontein</span>
            <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#D4AF37]" /> All loc lengths included</span>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-4">
            <CTA primary testid="hero-join-btn">Join The Membership</CTA>
            <a href="#plans" data-testid="hero-view-plans-btn"
              className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-8 py-4 rounded-full text-xs font-semibold uppercase tracking-widest hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300">
              View Membership Plans
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* SECTION 2 — INTRODUCTION */}
      <section className="py-24 md:py-32 px-6 bg-[#f4f1ea] text-[#0a0a0a]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-8">
                Loc care that fits your student life
              </h2>
              <p className="text-black/60 text-lg font-light leading-relaxed mb-4">
                Being a student shouldn&apos;t mean putting your loc care on hold.
              </p>
              <p className="text-black/60 text-lg font-light leading-relaxed mb-10">
                The Carlocs Student Membership makes consistent loc maintenance more affordable while giving you access to member-only savings on selected services and products.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {["Monthly Benefits", "All Loc Lengths", "Two Locations", "Member Savings"].map((h, i) => (
                  <div key={i} className="flex items-center gap-2 border-l-2 border-[#D4AF37] pl-4 py-1">
                    <span className="text-sm font-medium uppercase tracking-wider">{h}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="aspect-[4/5] overflow-hidden rounded-2xl">
              <motion.img src={IMG.intro} alt="Johannesburg locs care" className="w-full h-full object-cover"
                initial={{ scale: 1.15 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 3 — PLANS */}
      <section id="plans" className="py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-4">Membership Plans</p>
              <h2 className="font-serif text-4xl md:text-6xl tracking-tight">
                Choose Your <span className="italic text-[#D4AF37]">Plan</span>
              </h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8 items-start">
            {plans.map((plan, i) => <PlanCard key={plan.name} plan={plan} index={i} />)}
          </div>

          {/* Clarification box */}
          <Reveal delay={0.2}>
            <div className="mt-12 max-w-4xl mx-auto rounded-2xl border border-[#D4AF37]/30 bg-[#0d0d0d] p-8 md:p-10">
              <div className="flex items-start gap-4">
                <Info className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div className="space-y-4 text-white/60 text-sm font-light leading-relaxed">
                  <p>Your membership includes <span className="text-white font-medium">one eligible loc-care session per billing period</span> according to your selected plan.</p>
                  <p>Care and Premium members may choose between <span className="text-white">Retwist &amp; Style</span>, <span className="text-white">Crochet Maintenance</span> or <span className="text-white">Loc Treatment</span> for their included monthly session.</p>
                  <p>Additional services, hair, extensions and materials are charged separately where applicable.</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 4 — HOW IT WORKS */}
      <section className="py-24 md:py-32 px-6 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight mb-16 text-center">
              How Your Membership <span className="italic text-[#D4AF37]">Works</span>
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {steps.map((step, i) => (
              <motion.div key={step.num}
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="group rounded-2xl border border-white/10 p-8 hover:border-[#D4AF37]/50 transition-colors duration-500">
                <span className="font-serif text-5xl text-white/15 group-hover:text-[#D4AF37]/50 transition-colors duration-500">{step.num}</span>
                <h3 className="font-serif text-xl text-white mt-4 mb-2">{step.title}</h3>
                <p className="text-white/50 text-sm font-light">{step.desc}</p>
              </motion.div>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="text-center text-white/40 text-sm mt-12 max-w-2xl mx-auto">
              PayFast handles recurring membership payments. Fresha handles appointments and membership package benefits.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SECTION 5 — TWO LOCATIONS */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <h2 className="font-serif text-4xl md:text-6xl tracking-tight mb-5">
                One Membership. <span className="italic text-[#D4AF37]">Two Locations.</span>
              </h2>
              <p className="text-white/50 max-w-xl mx-auto">Your Carlocs Student Membership can be used at either Carlocs location.</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {["Johannesburg CBD", "Braamfontein"].map((loc, i) => (
              <motion.div key={loc}
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.12 }}
                className="group relative overflow-hidden rounded-2xl border border-white/10 hover:border-[#D4AF37]/60 transition-all duration-500 p-10 md:p-14 bg-gradient-to-br from-[#111] to-[#0a0a0a]">
                <MapPin className="w-10 h-10 text-[#D4AF37] mb-6" />
                <h3 className="font-serif text-3xl md:text-4xl">{loc}</h3>
                <div className="absolute -right-8 -bottom-8 w-40 h-40 border border-[#D4AF37]/10 rounded-full group-hover:scale-125 transition-transform duration-700" />
              </motion.div>
            ))}
          </div>
          <Reveal><p className="text-center text-white/40 text-sm">Choose the location that is most convenient when booking your appointment.</p></Reveal>
        </div>
      </section>

      {/* SECTION 6 — MEMBER BENEFITS */}
      <section className="py-24 md:py-32 px-6 bg-[#f4f1ea] text-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight mb-16 text-center">
              More Than Just <span className="italic text-[#9a7d1f]">Maintenance</span>
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {membBenefits.map((b, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-black/5 hover:shadow-xl transition-shadow duration-500">
                <span className="w-12 h-12 rounded-full bg-[#0a0a0a] flex items-center justify-center mb-6">
                  <b.icon className="w-5 h-5 text-[#D4AF37]" />
                </span>
                <h3 className="font-serif text-xl mb-3">{b.title}</h3>
                <p className="text-black/50 text-sm font-light leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — FAQ */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <div className="mb-14 text-center">
              <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-4">Membership Information</p>
              <h2 className="font-serif text-4xl md:text-6xl tracking-tight">Important <span className="italic text-[#D4AF37]">Details</span></h2>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              {faqs.map((faq, i) => (
                <FaqItem key={i} index={i} q={faq.q} a={faq.a} isOpen={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 8 — STUDENT ELIGIBILITY */}
      <section className="relative py-28 md:py-40 px-6 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={IMG.eligibility} alt="Johannesburg locs student member" className="w-full h-full object-cover object-center opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/70 to-[#050505]" />
        </div>
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight mb-6">
              Are You a <span className="italic text-[#D4AF37]">Current Student?</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-white/60 text-lg font-light max-w-xl mx-auto mb-10">
              The Carlocs Student Membership is designed for currently enrolled students. Valid student verification is required when joining.
            </p>
          </Reveal>
          <Reveal delay={0.2}><CTA primary testid="eligibility-apply-btn">Start Your Application</CTA></Reveal>
        </div>
      </section>

      {/* SECTION 9 — FINAL CTA */}
      <section className="py-24 md:py-40 px-6 bg-[#f4f1ea] text-[#0a0a0a]">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight mb-8">
              Your locs deserve consistent care.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-black/60 text-lg md:text-xl font-light max-w-2xl mx-auto mb-8">
              Join the Carlocs Student Membership and make professional loc care part of your monthly routine.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-serif text-2xl md:text-3xl text-[#9a7d1f] mb-10">From R349 / month</p>
          </Reveal>
          <Reveal delay={0.2}>
            <a href={JOTFORM_URL} target="_blank" rel="noopener noreferrer" data-testid="final-join-btn"
              className="group inline-flex items-center justify-center gap-2 bg-[#0a0a0a] text-white px-10 py-5 rounded-full text-xs font-semibold uppercase tracking-widest hover:bg-[#D4AF37] hover:text-[#050505] transition-all duration-300 hover:-translate-y-1">
              Join Carlocs Student Membership
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
            <p className="text-black/40 text-xs mt-6">Student verification required. Membership terms apply.</p>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5 bg-[#050505]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/"><img src={LOGO_URL} alt="Loctician Carlocs - Johannesburg loctician logo" className="h-10 w-auto" /></Link>
          <p className="text-white/30 text-sm">© {new Date().getFullYear()} Loctician Carlocs. All rights reserved.</p>
          <Link to="/" className="text-white/50 hover:text-[#D4AF37] transition-colors text-sm">Return to Main Site</Link>
        </div>
      </footer>

      <FloatingBookButton />
    </div>
  );
};

export default StudentMembershipPage;
